import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { parseClauseNo, clauseBasisOf } from '../components/otc/otcFields';

/**
 * MY — Allotment of Shares (Otherwise Than Cash, M32202) chatflow.
 *
 * 문서-뷰 Preset (CDD 미러링) — 한 task = 12문서 패키지(4단계).
 *   input-title → select-entity(필수, skip 불가) → confirm (오른쪽 패널에서 문서 입력 + Create Task)
 *   ── Person Lookup ── (Subscriber/Allottee/Secretary/Lodger/Director ✨)
 *   officer-role → officer-list → officer-detail
 *   ── Address 입력 ── address-input
 */
export const allotmentOtcFlow: ChatFlow = {
  id: 'allotment-shares-otc',
  taskCodeMatch: ['M32202'],
  categoryMatch: ['allotment of shares (otc)', 'allotment otc', 'otherwise than cash', 'allotment (otc)'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to the Malaysia **Allotment of Shares (Otherwise Than Cash)** preset.\n\n` +
          `The document package (${docCount} documents across 4 stages) is on the right. Fill the ` +
          `highlighted variables directly in the documents — click **Subscriber / Allottee / Secretary ✨** ` +
          `to look up and auto-fill a person, use the **📅** icon for dates and **📍** for addresses. ` +
          `Values are shared across every document, so filling once updates them all.\n\n` +
          `Use the stage stepper to move through **Board Approval → Subscription → Allotment Resolution → ` +
          `Statutory Lodgement**, and choose the **Subscriber Type** (Individual / Corporate) in Stage 2.\n\n` +
          `When everything is complete, click **Generate** to create the task and documents.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this allotment.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: false,
        analysis: {
          success: true, placeholder: '_entity', sourceResolver: 'ENTITY', officerRole: null,
          varKey: '_entity', varLabel: 'Entity', confidence: 1, message: 'Select Entity (Allotment OTC).',
          relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Your document package is ready to fill on the right. Use **✨** to look up people, ' +
        '**📅 / 📍** for dates and addresses, and **Generate** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Clause / Article No. 선택 (정관 결의 근거) ──
    {
      id: 'select-clause',
      aiMessage: 'Select a **Clause / Article No.** for the resolution, or type the number yourself.',
      widget: 'clause-number',
      widgetConfig: {
        targetVarKey: 'constitution_clause_number',
        targetEntityIndex: 0,
        clauseOptions: [],
        clauseDefaultBasis: 'constitution',
        analysis: {
          success: true, placeholder: 'constitution_clause_number', sourceResolver: 'SELECT', officerRole: null,
          varKey: 'constitution_clause_number', varLabel: 'Clause / Article No.', confidence: 1,
          message: 'Select or enter Clause / Article No.', relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    // ── Person Lookup ──
    { id: 'officer-role', aiMessage: 'Please select a role for the person you want to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    { id: 'officer-detail', aiMessage: 'Person details loaded. The document has been pre-filled.', widget: 'officer-field-picker', awaitEvent: true },
    // ── Address 직접 입력 ──
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
    { id: 'address-list', aiMessage: 'This person has multiple addresses. Pick the one to use.', widget: 'address-options', awaitEvent: true },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd, entityName, icpCd: entityCd,
          registrationNo: ent.registrationNo || '', entitySkipped: false, entityReselect: false,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      return {
        messages: [{ role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', entityName: '', icpCd: '', registrationNo: '', tlinkData: null, entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    // 정관 Clause / Article No. — 전용 clause-number 위젯 (AI 챗봇에서 선택/입력)
    if (eventName === 'request-person-lookup' && (payload?.fieldGroup === 'constitution-clause' || payload?.key === 'constitution_clause_number')) {
      const ctx = payload || {};
      const options = Array.isArray(ctx.clauseOptions)
        ? ctx.clauseOptions.map((o: any) => String(o ?? '').trim()).filter(Boolean)
        : [];
      return {
        messages: [{ role: 'user', content: 'Looking up **Clause / Article No.**…', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'select-clause',
        nextStepWidgetData: {
          targetVarKey: 'constitution_clause_number',
          clauseOptions: options,
          clauseDefaultBasis: ctx.defaultBasis || 'constitution',
          defaultValue: ctx.defaultValue || '',
          analysis: {
            success: true, placeholder: 'constitution_clause_number', sourceResolver: 'SELECT', officerRole: null,
            varKey: 'constitution_clause_number', varLabel: 'Clause / Article No.', confidence: 1,
            message: 'Select or enter Clause / Article No.', relatedFields: [], options,
          } as PlaceholderAnalysis,
        },
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!ctx.defaultRole) {
        return {
          messages: [{ role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      return {
        messages: [{ role: 'user', content: `Looking up **${ctx.defaultRole}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{ role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    if (eventName === 'request-address-input') {
      return {
        messages: [{ role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'address-input',
      };
    }

    // 정관 Clause / Article No. 선택 확정 → constitution_clause_number + basis (studio 는 applied-clause 로 패널 반영)
    if (eventName === 'clause-number-select') {
      const pick = payload || {};
      const clauseNo = String(pick.clauseNumber || '').trim();
      const rawOption = String(pick.rawOption || '').trim();
      const detected = clauseBasisOf(rawOption);
      const basis = pick.governingBasis || (detected !== 'other' ? detected : null) || (clauseNo ? 'constitution' : null);
      const label = basis === 'third_schedule' ? 'Third Schedule'
        : clauseNo ? `${basis === 'articles' ? 'Article' : 'Clause'} ${parseClauseNo(clauseNo) || clauseNo}` : 'Clause';
      const formUpdates: Record<string, any> = {
        constitution_clause_number: basis === 'third_schedule' ? '' : (parseClauseNo(clauseNo) || clauseNo),
        _clauseSyncAt: Date.now(),
        _clauseRawOption: rawOption,
      };
      return {
        messages: [{ role: 'user', content: `Governing document: **${label}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates,
        nextStepId: 'confirm',
      };
    }
    // legacy fallback — text-input value-submit on select-clause
    if (eventName === 'value-submit' && context.currentStepId === 'select-clause') {
      const raw = typeof payload === 'string' ? payload : payload?.value || payload;
      const rawStr = String(raw || '').trim();
      if (!rawStr) return {};
      const detected = clauseBasisOf(rawStr);
      return {
        messages: [{ role: 'user', content: `Clause / Article No.: **${rawStr}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          constitution_clause_number: detected === 'third_schedule' ? '' : (parseClauseNo(rawStr) || rawStr),
          _clauseSyncAt: Date.now(), _clauseRawOption: rawStr,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [{ role: 'user', content: `Choose an address for **${subject}**.`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [{ role: 'user', content: `Selected: **${opt.label || opt.type}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
